import moment from 'moment';
import { formatMoney, formatNumber, pickNumber } from '../utils/helpers';
import { getDailySalesColumns } from '../utils/constants';

const parseDailySales = (aggs = {}, opts = {}) => {
  const { startDate, endDate, currency = '' } = opts;

  const buckets = aggs?.by_day?.buckets || [];
  const map = new Map();
  buckets.forEach((b) => {
    const keyStr = b.key_as_string || (b.key ? moment(b.key).toISOString() : null);
    if (!keyStr) return;
    const dayIso = moment(keyStr).startOf('day').toISOString();
    map.set(dayIso, b);
  });

  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  const isValidRange = start.isValid() && end.isValid() && !end.isBefore(start);

  if (!isValidRange) {
    return {
      charts: {
        quantity: { labels: [], data: [] },
        money: { labels: [], values: [], costs: [], profits: [] }
      },
      tableRows: [],
      tableRowsRaw: [],
      mainColumns: getDailySalesColumns(),
      summary: { totalQuantity: 0, totalValue: 0, totalCost: 0, totalProfit: 0, currency }
    };
  }

  const labels = [];
  const quantityArr = [];
  const valueArr = [];
  const costArr = [];
  const profitArr = [];
  const tableRows = [];
  const tableRowsRaw = [];

  let totalQuantity = 0;
  let totalValue = 0;
  let totalCost = 0;
  let totalProfit = 0;

  const quantityNames = ['sales_count'];
  const valueNames = ['amount_sum'];
  const costNames = ['cost_sum'];
  const profitNames = ['profit'];

  for (let m = start.clone(); !m.isAfter(end); m.add(1, 'day')) {
    const dayIso = m.clone().startOf('day').toISOString();

    const displayDate = m.format('DD.MM.YYYY');

    labels.push(displayDate);

    const bucket = map.get(dayIso);

    const quantity = Number(pickNumber(bucket, quantityNames) || 0);
    const value = Number(pickNumber(bucket, valueNames) || 0);
    const cost = Number(pickNumber(bucket, costNames) || 0);
    const profit = Number(pickNumber(bucket, profitNames) || 0);

    quantityArr.push(quantity);
    valueArr.push(value);
    costArr.push(cost);
    profitArr.push(profit);

    totalQuantity += quantity;
    totalValue += value;
    totalCost += cost;
    totalProfit += profit;

    const rawRow = {
      date: displayDate,
      quantity: formatNumber(quantity),
      value: formatMoney(value, currency),
      cost: formatMoney(cost, currency),
      profit: formatMoney(profit, currency)
    };

    const formattedRow = [
      rawRow.date,
      rawRow.quantity,
      rawRow.value,
      rawRow.cost,
      rawRow.profit
    ];

    const allEmpty = [quantity, value, cost, profit].every(v => v === 0 || v == null || Number.isNaN(v));
    if (!allEmpty) {
      tableRows.push(formattedRow);
      tableRowsRaw.push(rawRow);
    }
  }

  const topTotalQuantity = (aggs.total_sales_count && aggs.total_sales_count.value) ?? null;
  const topTotalValue = (aggs.total_amount && aggs.total_amount.value) ?? null;
  const topTotalCost = (aggs.total_cost && aggs.total_cost.value) ?? null;
  const topTotalProfit = (aggs.total_profit && aggs.total_profit.value) ?? null;

  const totalQty = topTotalQuantity != null ? Number(topTotalQuantity) : totalQuantity;
  const totalVal = topTotalValue != null ? Number(topTotalValue) : totalValue;
  const totalCst = topTotalCost != null ? Number(topTotalCost) : totalCost;
  const totalPrft = topTotalProfit != null ? Number(topTotalProfit) : totalProfit;

  const summary = {
    totalQuantity: totalQty,
    totalValue: totalVal,
    totalCost: totalCst,
    totalProfit: totalPrft,
    totalQuantityStr: totalQty.toLocaleString(),
    totalValueStr: formatMoney(totalVal, currency),
    totalCostStr: formatMoney(totalCst, currency),
    totalProfitStr: formatMoney(totalPrft, currency),
    currency
  };

  const charts = {
    quantity: { labels, data: quantityArr },
    money: { labels, values: valueArr, costs: costArr, profits: profitArr }
  };

  const mainColumns = getDailySalesColumns();

  return { charts, tableRows, tableRowsRaw, summary, mainColumns };
}

export default parseDailySales;